import { useCallback, useEffect, useMemo, useState } from "react";
import { Fuel, Zap, Wrench, ShieldAlert, Receipt, FileCheck2, Sparkles, Clock, ChevronDown, Loader2, RefreshCw, Gauge } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import TimelineEventDetail from "@/components/TimelineEventDetail";

const KIND_META = {
  fuel:     { icon: Fuel,        label: "Refuel",           accent: "#FF5A1F" },
  charging: { icon: Zap,         label: "EV Charging",      accent: "#22D3EE" },
  routine:  { icon: Wrench,      label: "Routine Service",  accent: "#FBBF24" },
  repair:   { icon: ShieldAlert, label: "Repair",           accent: "#EF4444" },
  bill:     { icon: Receipt,     label: "Bill",             accent: "#F472B6" },
  document: { icon: FileCheck2,  label: "Document",         accent: "#34D399" },
};

const KIND_FILTERS = [
  { key: "all",      label: "All events" },
  { key: "fuel",     label: "Refuels" },
  { key: "charging", label: "Charging" },
  { key: "routine",  label: "Service" },
  { key: "repair",   label: "Repairs" },
  { key: "bill",     label: "Bills" },
  { key: "document", label: "Documents" },
];

function fmtDate(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d)) return iso.slice(0, 10);
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

function fmtMoney(v) {
  if (v == null || v === 0) return null;
  return `₹${Number(v).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

export default function Timeline() {
  const [vehicles, setVehicles] = useState([]);
  const [vehicleId, setVehicleId] = useState("");   // "" = All Vehicles
  const [kindFilter, setKindFilter] = useState("all");
  const [events, setEvents] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [openEvent, setOpenEvent] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const qs = vehicleId ? `?vehicle_id=${encodeURIComponent(vehicleId)}` : "";
      const { data } = await api.get(`/reports/timeline${qs}`);
      setVehicles(data.vehicles || []);
      setEvents(data.events || []);
      setTotals(data.totals || {});
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [vehicleId]);

  useEffect(() => { load(); }, [load]);

  // Refresh whenever the user returns to this tab (so newly-added
  // refuels/charges/services show up without a manual reload).
  useEffect(() => {
    const onFocus = () => load();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [load]);

  const visibleEvents = useMemo(() => {
    if (kindFilter === "all") return events;
    return events.filter((e) => e.kind === kindFilter);
  }, [events, kindFilter]);

  return (
    <div className="max-w-4xl mx-auto space-y-6" data-testid="timeline-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="ft-overline text-xs">HISTORY</div>
          <h1 className="font-display text-3xl sm:text-4xl font-black tracking-tighter">Timeline</h1>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Every refuel, charge, service, repair and document across your fleet — newest first.
          </p>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="inline-flex items-center gap-2 px-3 py-2 rounded-lg text-sm border border-[var(--border-line)] hover:bg-[var(--hover)] text-[var(--text-primary)] disabled:opacity-50"
          data-testid="timeline-refresh"
        >
          {loading ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />} Refresh
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        {/* Vehicle picker */}
        <div className="relative">
          <select
            value={vehicleId}
            onChange={(e) => setVehicleId(e.target.value)}
            className="appearance-none pl-4 pr-9 py-2 rounded-lg border border-[var(--border-line)] bg-[var(--bg-elevated)] text-sm font-semibold text-[var(--text-primary)] focus:outline-none focus:border-[#FF5A1F] min-w-[180px]"
            data-testid="timeline-vehicle-filter"
          >
            <option value="">All Vehicles ({vehicles.length})</option>
            {vehicles.map((v) => (
              <option key={v.id} value={v.id}>
                {v.number}{v.name ? ` — ${v.name}` : ""}
              </option>
            ))}
          </select>
          <ChevronDown size={14} className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
        </div>

        {/* Kind filter chips */}
        <div className="flex flex-wrap gap-2" data-testid="timeline-kind-chips">
          {KIND_FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setKindFilter(f.key)}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold transition-colors ${
                kindFilter === f.key
                  ? "bg-[#FF5A1F] text-black"
                  : "border border-[var(--border-line)] text-[var(--text-secondary)] hover:text-[var(--text-primary)] hover:bg-[var(--hover)]"
              }`}
              data-testid={`timeline-chip-${f.key}`}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {/* Totals strip */}
      {!loading && events.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 text-xs" data-testid="timeline-totals">
          <TotalPill label="Events" value={totals.count} />
          <TotalPill label="Refuels" value={totals.refuel} icon={Fuel} accent="#FF5A1F" />
          <TotalPill label="Charging" value={totals.charging} icon={Zap} accent="#22D3EE" />
          <TotalPill label="Service" value={totals.service} icon={Wrench} accent="#FBBF24" />
          <TotalPill label="Bills" value={totals.bill} icon={Receipt} accent="#F472B6" />
        </div>
      )}

      {/* Events */}
      {loading ? (
        <div className="p-12 flex items-center justify-center text-[var(--text-muted)] gap-2">
          <Loader2 size={18} className="animate-spin" /> Loading timeline…
        </div>
      ) : visibleEvents.length === 0 ? (
        <div className="rounded-xl border border-dashed border-[var(--border-line)] p-10 text-center" data-testid="timeline-empty">
          <Clock size={32} className="mx-auto text-[var(--text-muted)] mb-3" />
          <div className="font-display text-xl font-bold">Nothing here yet</div>
          <p className="text-sm text-[var(--text-muted)] mt-1">
            Add a refuel, log a charging session or upload a service bill to see it appear here.
          </p>
        </div>
      ) : (
        <div className="relative pl-0 sm:pl-4">
          {/* vertical rail */}
          <div className="hidden sm:block absolute left-1 top-2 bottom-2 w-px bg-[var(--border-line)]" aria-hidden />
          <div className="space-y-3">
            {visibleEvents.map((e) => (
              <TimelineCard key={`${e.record_type}-${e.id}`} event={e} onOpen={setOpenEvent} />
            ))}
          </div>
        </div>
      )}

      {openEvent && <TimelineEventDetail event={openEvent} onClose={() => setOpenEvent(null)} />}
    </div>
  );
}

function TotalPill({ label, value, icon: Icon, accent }) {
  return (
    <div className="rounded-lg border border-[var(--border-line)] bg-[var(--bg-surface)] px-3 py-2 flex items-center justify-between gap-2">
      <div className="ft-overline text-[9px] flex items-center gap-1" style={{ color: accent || undefined }}>
        {Icon ? <Icon size={12} /> : null} {label}
      </div>
      <div className="font-mono text-sm font-bold text-[var(--text-primary)]">{value ?? 0}</div>
    </div>
  );
}

function TimelineCard({ event, onOpen }) {
  const meta = KIND_META[event.kind] || KIND_META.document;
  const Icon = meta.icon;
  const amount = fmtMoney(event.amount);
  const qty = event.quantity != null ? `${event.quantity} ${event.quantity_unit || ""}` : null;

  return (
    <button
      onClick={() => onOpen(event)}
      className="w-full text-left group relative flex items-start gap-3 sm:gap-4 border border-[var(--border-line)] bg-[var(--bg-surface)] hover:bg-[var(--hover)] rounded-xl p-4 transition-colors"
      data-testid={`timeline-card-${event.record_type}-${event.id}`}
      data-kind={event.kind}
    >
      {/* Rail dot (desktop) */}
      <div className="hidden sm:block absolute -left-4 top-6 w-2 h-2 rounded-full ring-4 ring-[var(--bg-base)]" style={{ background: meta.accent }} />

      <div
        className="w-11 h-11 rounded-lg flex items-center justify-center flex-shrink-0"
        style={{ background: meta.accent, color: "#000" }}
      >
        <Icon size={20} strokeWidth={2.4} />
      </div>

      <div className="flex-1 min-w-0">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="ft-overline text-[10px] mb-0.5">{meta.label}</div>
            <div className="font-display text-base sm:text-lg font-black tracking-tight truncate">
              {event.vehicle?.number || "Vehicle"} — {event.title}
            </div>
            <div className="text-xs text-[var(--text-muted)] font-mono mt-1 flex flex-wrap items-center gap-x-3 gap-y-1">
              <span>{fmtDate(event.date)}</span>
              {event.odometer != null && (
                <span className="inline-flex items-center gap-1"><Gauge size={11} /> {event.odometer.toLocaleString()} km</span>
              )}
              {qty && <span>· {qty}</span>}
              {event.category && <span>· {event.category}</span>}
              {event.subtitle && <span className="truncate max-w-xs">· {event.subtitle}</span>}
            </div>
          </div>
          {amount && (
            <div className="font-mono text-sm sm:text-base font-bold text-[var(--text-primary)] flex-shrink-0">
              {amount}
            </div>
          )}
        </div>

        {event.ai_alert && (
          <div
            className="mt-2 inline-flex items-center gap-2 px-2 py-1 rounded text-[11px] font-semibold"
            style={{ background: "#FF5A1F22", color: "#FF7A3A" }}
            data-testid="timeline-card-ai-alert"
          >
            <Sparkles size={11} /> {event.ai_alert}
          </div>
        )}
      </div>
    </button>
  );
}
